import React, { useEffect, useRef, useState } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { ReportPeriodSelector, useReportPeriod } from './ReportPeriodSelector';
import { TeamCalendarSummary } from './TeamCalendarSummary';

export const TeamPerformanceReport = ({ teamId, teamName, onClose }) => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;
  const period = useReportPeriod(api);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(null);
  const apiRef = useRef(api); apiRef.current = api;
  const { from, to, valid, periodType } = period;
  useEffect(() => {
    setData(null); setError(false);
    if (!valid) { setLoading(false); return undefined; }
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => apiRef.current.post('/premium-reports/team/data', {
      team_id: teamId, period_type: periodType, date_from: from, date_to: to, language,
    }, { signal: controller.signal }).then(res => { if (active) setData(res.data); })
      .catch(err => { if (active && err.code !== 'ERR_CANCELED') setError(true); })
      .finally(() => { if (active) setLoading(false); }), 200);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [teamId, from, to, valid, periodType, language]);
  const current = data && valid && data.period.start_date === from && data.period.end_date === to;
  const download = async format => {
    if (!current || loading || exporting) return;
    setExporting(format);
    try {
      const response = await api.post(`/premium-reports/team${format === 'xlsx' ? '/excel' : ''}`, {
        team_id: teamId, period_type: periodType, date_from: from, date_to: to, language,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a'); anchor.href = url;
      anchor.download = `team_performance_${from}_${to}.${format}`; anchor.click(); URL.revokeObjectURL(url);
    } catch { toast.error(t('Export failed.', 'Esportazione non riuscita.')); }
    finally { setExporting(null); }
  };
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent closeTestId="team-performance-dismiss" className="w-[calc(100%_-_2rem)] max-w-3xl max-h-[90vh] overflow-y-auto bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="team-performance-dialog">
      <DialogHeader className="min-w-0 pr-5 break-words">
        <DialogTitle data-testid="team-performance-title">{t('Team Performance Report', 'Report Performance Squadra')} — {teamName}</DialogTitle>
        <DialogDescription data-testid="team-performance-visible-period">{from || '—'} → {to || '—'}</DialogDescription>
      </DialogHeader>
      <ReportPeriodSelector period={period} t={t} testId="team-performance-period" />
      {(loading || period.loading) && <p role="status" data-testid="team-performance-loading"><Loader2 className="inline w-4 h-4 animate-spin mr-2" />{t('Loading…', 'Caricamento…')}</p>}
      {error && <p role="alert" className="text-red-300" data-testid="team-performance-error">{t('Report could not be loaded.', 'Impossibile caricare il report.')}</p>}
      {current && <>
        <p className="text-sm" data-testid="team-performance-total-load">{t('Total Team Load', 'Carico totale squadra')}: <b>{data.load.total_team_load.toLocaleString()} AU</b></p>
        <p className="text-xs text-zinc-400" data-testid="team-performance-occurrences">{t('Athlete-session occurrences', 'Occorrenze atleta-sessione')}: {data.load.total_athlete_sessions}</p>
        <TeamCalendarSummary calendar={data.team_calendar} t={t} testId="team-performance-calendar" />
      </>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={onClose} data-testid="team-performance-close">{t('Close', 'Chiudi')}</Button>
        <Button onClick={() => download('pdf')} disabled={!current || loading || !!exporting} data-testid="team-performance-export-pdf"><Download className="w-4 h-4 mr-2" />{exporting === 'pdf' ? '…' : 'PDF'}</Button>
        <Button onClick={() => download('xlsx')} disabled={!current || loading || !!exporting} data-testid="team-performance-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-2" />{exporting === 'xlsx' ? '…' : 'XLSX'}</Button>
      </div>
    </DialogContent>
  </Dialog>;
};